import React, { useState, useEffect } from 'react';
import LandingPage from './pages/LandingPage';
import QuestionnairePage from './pages/QuestionnairePage';
import ResultsPage from './pages/ResultsPage';
import PricingPage from './pages/PricingPage';
import MyAccountPage from './pages/MyAccountPage';
import LoadingAnimation from './components/LoadingAnimation';
import AuthDialog from './components/AuthDialog';
import VIPCodeDialog from './components/VIPCodeDialog';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

export default function App() {
  const [currentPage, setCurrentPage] = useState('landing'); // 'landing', 'questionnaire', 'results', 'loading', 'pricing', 'account'
  const [userAnswers, setUserAnswers] = useState(null);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [preSelectedCategory, setPreSelectedCategory] = useState(null);
  
  // User authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showVIPDialog, setShowVIPDialog] = useState(false);
  const [vipCodeAsked, setVipCodeAsked] = useState(false);
  
  // Gamification state
  const [xpPoints, setXpPoints] = useState(0);
  const [promptsGenerated, setPromptsGenerated] = useState(0);
  
  // Load user from local storage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('promptcraft_user');
    const vipAsked = localStorage.getItem('promptcraft_vip_asked');
    
    console.log('Loading saved user from localStorage:', savedUser);
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Parsed user data:', userData);
        setCurrentUser(userData);
        setPromptsGenerated(userData.prompts_used || 0);
      } catch (error) {
        console.error('Error loading user:', error);
        localStorage.removeItem('promptcraft_user');
      }
    }
    
    if (vipAsked === 'true') {
      setVipCodeAsked(true);
    }
  }, []);
  
  // Debug: Log currentUser state changes
  useEffect(() => {
    console.log('Current user state changed:', currentUser);
  }, [currentUser]);

  const handleStartQuestionnaire = (category = null) => {
    // Check if user is logged in
    if (!currentUser) {
      setShowAuthDialog(true);
      setPreSelectedCategory(category);
      return;
    }
    
    // Check if free user has reached limit
    if (!currentUser.is_pro && currentUser.prompts_used >= 10) {
      toast.error('Limit reached! Enter VIP code to continue.');
      return;
    }
    
    setPreSelectedCategory(category);
    setCurrentPage('questionnaire');
  };

  const handleSubmitQuestionnaire = async (answers) => {
    setUserAnswers(answers);
    
    // Show loading animation
    setCurrentPage('loading');
    
    try {
      // Generate prompt template based on answers
      const promptTemplate = generatePrompt(answers);
      
      // Call backend API to generate AI content
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt_template: promptTemplate,
          use_case: answers.useCase,
          outcome: answers.outcome,
          user_id: currentUser?.id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate AI content');
      }
      
      const data = await response.json();
      
      // Set the AI-generated content
      setGeneratedPrompt(data.generated_content);
      
      // Award XP
      setXpPoints(prev => prev + 50);
      setPromptsGenerated(prev => prev + 1);
      
      // Update user prompts count in local state
      if (currentUser && !currentUser.is_pro) {
        const updatedUser = { ...currentUser, prompts_used: (currentUser.prompts_used || 0) + 1 };
        setCurrentUser(updatedUser);
        localStorage.setItem('promptcraft_user', JSON.stringify(updatedUser));
      }
      
      setCurrentPage('results');
    } catch (error) {
      console.error('Error generating AI content:', error);
      // Fallback to showing the prompt template if API fails
      const promptTemplate = generatePrompt(answers);
      setGeneratedPrompt(promptTemplate);
      setXpPoints(prev => prev + 50);
      setPromptsGenerated(prev => prev + 1);
      setCurrentPage('results');
    }
  };

  const handleBackToHome = () => {
    setPreSelectedCategory(null);
    setCurrentPage('landing');
  };
  
  const handleAuthSuccess = (userData) => {
    console.log('handleAuthSuccess called with:', userData);
    
    // Force state update
    setCurrentUser({...userData});
    setPromptsGenerated(userData.prompts_used || 0);
    
    // Make sure we're on landing page
    if (currentPage !== 'landing' && !preSelectedCategory) {
      setCurrentPage('landing');
    }
    
    // Show VIP code dialog on first login if not asked before
    if (!vipCodeAsked && !userData.is_pro) {
      setTimeout(() => {
        console.log('Showing VIP dialog');
        setShowVIPDialog(true);
      }, 500);
    } else {
      // Continue to questionnaire if category was pre-selected
      if (preSelectedCategory) {
        setCurrentPage('questionnaire');
      }
    }
  };
  
  const handleVIPSuccess = async () => {
    // Reload user data
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/user/${currentUser.id}`);
      const userData = await response.json();
      
      setCurrentUser(userData);
      localStorage.setItem('promptcraft_user', JSON.stringify(userData));
      localStorage.setItem('promptcraft_vip_asked', 'true');
      setVipCodeAsked(true);
      setShowVIPDialog(false);
      
      // Show congratulations message
      setTimeout(() => {
        toast.success('🎉 Congratulations! You\'re now a Pro Member with unlimited prompts!', {
          duration: 5000
        });
      }, 500);
      
      // Continue to questionnaire
      if (preSelectedCategory) {
        setCurrentPage('questionnaire');
      }
    } catch (error) {
      console.error('Error reloading user:', error);
    }
  };
  
  const handleVIPSkip = () => {
    localStorage.setItem('promptcraft_vip_asked', 'true');
    setVipCodeAsked(true);
    
    // Continue to questionnaire
    if (preSelectedCategory) {
      setCurrentPage('questionnaire');
    }
  };
  
  const handleLogout = () => {
    // Clear user data
    setCurrentUser(null);
    setPromptsGenerated(0);
    setXpPoints(0);
    setVipCodeAsked(false);
    
    // Clear local storage
    localStorage.removeItem('promptcraft_user');
    localStorage.removeItem('promptcraft_vip_asked');
    
    // Return to landing page
    setCurrentPage('landing');
    
    console.log('User logged out');
  };

  const handleGenerateNewPrompt = () => {
    setPreSelectedCategory(null);
    setCurrentPage('questionnaire');
  };

  const handleViewPricing = () => {
    setCurrentPage('pricing');
  };
  
  const handleViewAccount = () => {
    setCurrentPage('account');
  };

  return (
    <div className="min-h-screen">
      {currentPage === 'landing' && (
        <LandingPage 
          onGetStarted={handleStartQuestionnaire}
          onViewPricing={handleViewPricing}
          xpPoints={xpPoints}
          promptsGenerated={promptsGenerated}
          currentUser={currentUser}
          onShowVIPDialog={() => setShowVIPDialog(true)}
          onLogout={handleLogout}
          onViewAccount={handleViewAccount}
        />
      )}
      {currentPage === 'questionnaire' && (
        <QuestionnairePage 
          onSubmit={handleSubmitQuestionnaire}
          onBack={handleBackToHome}
          preSelectedCategory={preSelectedCategory}
        />
      )}
      {currentPage === 'loading' && (
        <LoadingAnimation />
      )}
      {currentPage === 'results' && (
        <ResultsPage 
          userAnswers={userAnswers}
          generatedPrompt={generatedPrompt}
          onGenerateNew={handleGenerateNewPrompt}
          onBackToHome={handleBackToHome}
          xpPoints={xpPoints}
        />
      )}
      {currentPage === 'pricing' && (
        <PricingPage 
          onBackToHome={handleBackToHome}
          currentUser={currentUser}
          onShowVIPDialog={() => setShowVIPDialog(true)}
        />
      )}
      {currentPage === 'account' && (
        <MyAccountPage 
          currentUser={currentUser}
          onBackToHome={handleBackToHome}
          onShowVIPDialog={() => setShowVIPDialog(true)}
        />
      )}
      
      {/* Authentication Dialog */}
      <AuthDialog 
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onAuthSuccess={handleAuthSuccess}
      />
      
      {/* VIP Code Dialog */}
      {currentUser && (
        <VIPCodeDialog 
          open={showVIPDialog}
          onOpenChange={setShowVIPDialog}
          userId={currentUser.id}
          onSuccess={handleVIPSuccess}
          onSkip={handleVIPSkip}
        />
      )}
      
      <Toaster />
    </div>
  );
}

// Enhanced prompt generation logic with smart adaptation
function generatePrompt(answers) {
  const { useCase, outcome, experienceLevel, targetAudience, tone, constraints } = answers;
  
  // Detect if this is an image generation prompt
  const isImagePrompt = useCase === 'Image Generation' || 
    outcome.toLowerCase().includes('image') || 
    outcome.toLowerCase().includes('visual') ||
    outcome.toLowerCase().includes('picture') ||
    outcome.toLowerCase().includes('illustration');
  
  // Detect if this is a video generation prompt
  const isVideoPrompt = useCase === 'Video Creation' || 
    outcome.toLowerCase().includes('video') ||
    outcome.toLowerCase().includes('animation');
  
  // Detect if this is a code development prompt
  const isCodePrompt = useCase === 'Code Development' || 
    outcome.toLowerCase().includes('code') ||
    outcome.toLowerCase().includes('programming') ||
    outcome.toLowerCase().includes('development');

  if (isImagePrompt) {
    return generateImagePrompt(answers);
  } else if (isVideoPrompt) {
    return generateVideoPrompt(answers);
  } else if (isCodePrompt) {
    return generateCodePrompt(answers);
  } else {
    return generateStandardPrompt(answers);
  }
}

function generateImagePrompt(answers) {
  const { outcome, experienceLevel, tone, constraints } = answers;
  
  // Create an ultra-detailed, copy-paste ready prompt for the AI tool
  let prompt = `Generate a highly detailed image based on this description: ${outcome}\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📐 COMPOSITION & FRAMING\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Camera Angle: Choose the most compelling perspective (eye-level for relatability, low-angle for drama, high-angle for overview, Dutch angle for tension)\n`;
  prompt += `• Shot Type: Determine optimal framing (extreme close-up for detail, close-up for emotion, medium shot for context, wide shot for environment, extreme wide for scale)\n`;
  prompt += `• Rule of Thirds: Position key elements at intersection points for visual balance\n`;
  prompt += `• Leading Lines: Use natural or architectural lines to guide viewer's eye to focal point\n`;
  prompt += `• Depth Layers: Establish clear foreground, midground, and background elements\n`;
  prompt += `• Focal Point: Make the main subject immediately clear and commanding\n`;
  prompt += `• Negative Space: Use empty space strategically to emphasize subject\n`;
  prompt += `• Symmetry/Asymmetry: Choose based on desired emotional impact\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎨 VISUAL STYLE & AESTHETICS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  
  // Detailed style based on tone
  if (tone === 'Professional') {
    prompt += `• Art Style: Photorealistic with corporate polish, clean lines, minimalist approach\n`;
    prompt += `• Color Palette: Sophisticated neutrals (charcoal, slate, ivory, soft blues) with strategic accent colors\n`;
    prompt += `• Visual Mood: Confident, trustworthy, premium quality, business-appropriate\n`;
    prompt += `• Texture: Smooth, refined surfaces with subtle material details\n`;
    prompt += `• Typography (if applicable): Modern sans-serif, clean hierarchy, plenty of whitespace\n`;
  } else if (tone === 'Creative') {
    prompt += `• Art Style: Bold artistic interpretation - consider impressionist, abstract, surreal, or avant-garde approaches\n`;
    prompt += `• Color Palette: Vibrant, saturated colors with unexpected combinations - use color psychology to evoke emotion\n`;
    prompt += `• Visual Mood: Imaginative, energetic, unconventional, thought-provoking, expressive\n`;
    prompt += `• Texture: Rich, varied textures - painterly strokes, mixed media effects, experimental techniques\n`;
    prompt += `• Artistic Elements: Incorporate unexpected visual metaphors, symbolic imagery, creative distortions\n`;
  } else if (tone === 'Casual') {
    prompt += `• Art Style: Natural, authentic, slice-of-life realism with approachable aesthetic\n`;
    prompt += `• Color Palette: Warm, inviting tones (earth tones, soft pastels, natural colors) that feel comfortable\n`;
    prompt += `• Visual Mood: Friendly, relatable, down-to-earth, welcoming, authentic\n`;
    prompt += `• Texture: Organic, natural textures that feel real and tactile\n`;
    prompt += `• Atmosphere: Create sense of familiarity and comfort\n`;
  } else if (tone === 'Technical') {
    prompt += `• Art Style: Precision technical illustration, blueprint quality, scientific accuracy\n`;
    prompt += `• Color Palette: Functional colors - technical blues, engineering grays, highlight accents for emphasis\n`;
    prompt += `• Visual Mood: Analytical, systematic, precise, informative, authoritative\n`;
    prompt += `• Texture: Clean, precise surfaces with exact material representation\n`;
    prompt += `• Detail Level: Include technical specifications, measurements, cross-sections where relevant\n`;
  }
  
  prompt += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `💡 LIGHTING & ATMOSPHERE\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Primary Light Source: Define direction, intensity, and color temperature (warm/cool)\n`;
  prompt += `• Light Quality: Specify soft/diffused for flattering effects or hard/directional for drama and contrast\n`;
  prompt += `• Time of Day: Golden hour (warm, flattering), blue hour (cool, mysterious), midday (bright, clear), night (dramatic, moody)\n`;
  prompt += `• Shadow Treatment: Deep shadows for mystery, soft shadows for gentleness, no shadows for flat commercial look\n`;
  prompt += `• Rim Lighting: Add edge lighting to separate subject from background\n`;
  prompt += `• Color Temperature: Warm (inviting, cozy) vs Cool (modern, clinical) vs Mixed (complex, interesting)\n`;
  prompt += `• Atmospheric Effects: Consider fog, mist, dust particles, lens flares, god rays for depth and mood\n`;
  prompt += `• Light and Shadow Balance: Ensure proper exposure across all elements\n`;
  prompt += `• Volumetric Lighting: Use light beams, atmospheric scattering for three-dimensional feel\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎯 SUBJECT DETAILS & ELEMENTS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Main Subject: Render with maximum detail, clarity, and presence\n`;
  prompt += `• Material Properties: Define textures precisely (metallic sheen, fabric weave, wood grain, glass transparency, liquid reflections)\n`;
  prompt += `• Surface Details: Include fine details like scratches, weathering, reflections, subsurface scattering\n`;
  prompt += `• Scale and Proportion: Ensure anatomically/architecturally correct proportions\n`;
  prompt += `• Interaction with Environment: Show how subject relates to surroundings (shadows cast, reflections, environmental interaction)\n`;
  prompt += `• Secondary Elements: Include supporting elements that enhance composition without distracting\n`;
  prompt += `• Micro Details: Add subtle elements that reward close inspection (dust particles, fabric texture, surface imperfections)\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `⚙️ TECHNICAL PARAMETERS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Resolution: 8K ultra-high definition, maximum detail retention\n`;
  prompt += `• Aspect Ratio: 16:9 for landscapes/cinematic, 1:1 for social media, 9:16 for mobile/vertical, 3:2 for photography\n`;
  prompt += `• Depth of Field: Shallow DoF for subject isolation, deep DoF for landscape detail, selective focus for storytelling\n`;
  prompt += `• Bokeh Quality: If using shallow DoF, create smooth, aesthetically pleasing background blur\n`;
  prompt += `• Focus Point: Crystal sharp focus on main subject with gradual falloff\n`;
  prompt += `• Color Grading: Professional color correction with balanced saturation and contrast\n`;
  prompt += `• Dynamic Range: Preserve detail in both highlights and shadows\n`;
  prompt += `• Noise/Grain: Clean image or intentional film grain for aesthetic\n`;
  prompt += `• Sharpness: Tack-sharp on focal plane, natural falloff elsewhere\n\n`;
  
  if (constraints && constraints.trim()) {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `⚠️ CRITICAL USER CONSTRAINTS - MUST BE APPLIED\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `The following constraints are MANDATORY and override all other considerations:\n\n`;
    prompt += `${constraints}\n\n`;
    prompt += `**HOW TO APPLY THESE CONSTRAINTS:**\n`;
    prompt += `• Integrate these requirements into EVERY aspect of the image generation\n`;
    prompt += `• If a constraint specifies a style, dimension, or element - include it in the main prompt\n`;
    prompt += `• If a constraint excludes something - add it to the negative prompt\n`;
    prompt += `• If a constraint specifies technical parameters - adjust the technical specifications section\n`;
    prompt += `• Review your final prompt to ensure ALL constraints are addressed\n\n`;
    prompt += `⚠️ VALIDATION: Before finalizing, verify that your image prompt will produce results that satisfy ALL constraints above.\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `✨ QUALITY ENHANCEMENT KEYWORDS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `masterpiece, best quality, ultra-detailed, 8K resolution, professional photography, award-winning composition, `;
  prompt += `highly detailed, perfect lighting, sharp focus, studio quality, crisp and clean, photorealistic, `;
  prompt += `trending on ArtStation, professionally color graded, cinematic lighting, HDR, ray tracing, `;
  prompt += `volumetric lighting, realistic textures, physically-based rendering\n\n`;
  
  if (experienceLevel === 'Advanced') {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `🔬 ADVANCED RENDERING TECHNIQUES\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `• Global Illumination: Accurate light bounce and color bleeding for photorealistic lighting\n`;
    prompt += `• Subsurface Scattering: For translucent materials (skin, wax, marble)\n`;
    prompt += `• Ambient Occlusion: Subtle shadows in crevices and contact points for depth\n`;
    prompt += `• Physically-Based Rendering: Use PBR materials for realistic metal, glass, fabric\n`;
    prompt += `• Color Grading: Apply cinematic color grading with teal and orange or complementary color schemes\n`;
    prompt += `• Chromatic Aberration: Subtle lens distortion for photographic realism (use sparingly)\n`;
    prompt += `• Atmospheric Perspective: Objects further away become hazier and cooler in tone\n`;
    prompt += `• Micro-Contrast: Enhance local contrast for perceived sharpness\n`;
    prompt += `• Spectral Rendering: Accurate wavelength-based color representation\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🚫 NEGATIVE PROMPT (Avoid These)\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `blurry, out of focus, low quality, low resolution, pixelated, jpeg artifacts, compression artifacts, `;
  prompt += `distorted, deformed, disfigured, bad anatomy, incorrect proportions, ugly, poorly drawn, bad art, `;
  prompt += `amateur, watermark, signature, username, text, logo, brand name, copyright, `;
  prompt += `cropped, cut off, out of frame, border, duplicate, cloned, repeated, `;
  prompt += `worst quality, low res, normal quality, bad lighting, overexposed, underexposed, `;
  prompt += `mutated, extra limbs, missing limbs, floating limbs, disconnected limbs, malformed, `;
  prompt += `gross proportions, long neck, cross-eyed, mutated hands, poorly rendered face, `;
  prompt += `bad shadows, flat, two-dimensional, lack of depth, monochrome (unless intended), grainy (unless intended)`;
  
  return prompt;
}

function generateVideoPrompt(answers) {
  const { outcome, experienceLevel, targetAudience, tone, constraints } = answers;
  
  let prompt = `Create a professional video production for: ${outcome}\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎬 VIDEO PRODUCTION SPECIFICATIONS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Target Audience: ${targetAudience}\n`;
  prompt += `• Visual Style: ${tone}\n`;
  prompt += `• Production Quality: Professional, broadcast-ready\n`;
  if (constraints && constraints.trim()) {
    prompt += `\n⚠️ MANDATORY REQUIREMENTS:\n`;
    prompt += `${constraints}\n`;
    prompt += `⚠️ ALL video elements must comply with these requirements.\n`;
  }
  prompt += `\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎨 VISUAL STYLE & AESTHETIC DIRECTION\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  if (tone === 'Professional') {
    prompt += `• Overall Aesthetic: Clean, corporate, polished with minimalist design principles\n`;
    prompt += `• Color Palette: Sophisticated blues, grays, whites with strategic accent colors\n`;
    prompt += `• Typography: Modern sans-serif, clear hierarchy, ample whitespace\n`;
    prompt += `• Motion Style: Smooth, purposeful animations - avoid flashy effects\n`;
    prompt += `• Visual Elements: Infographics, data visualizations, clean icons\n`;
    prompt += `• Transitions: Professional cuts, subtle fades, geometric wipes\n`;
    prompt += `• Camera Work: Steady, professional shots - no shaky cam\n`;
    prompt += `• Pace: Measured, confident, allows time for information absorption\n`;
  } else if (tone === 'Creative') {
    prompt += `• Overall Aesthetic: Bold, innovative, artistically expressive\n`;
    prompt += `• Color Palette: Vibrant, saturated colors with unexpected combinations\n`;
    prompt += `• Typography: Creative fonts, dynamic text animations, kinetic typography\n`;
    prompt += `• Motion Style: Dynamic, energetic movements with creative transitions\n`;
    prompt += `• Visual Elements: Abstract shapes, creative illustrations, mixed media\n`;
    prompt += `• Transitions: Creative wipes, morphing effects, seamless scene blends\n`;
    prompt += `• Camera Work: Creative angles, deliberate camera movements, unique perspectives\n`;
    prompt += `• Pace: Energetic, quick cuts, maintains viewer excitement\n`;
  } else if (tone === 'Casual') {
    prompt += `• Overall Aesthetic: Friendly, approachable, authentic feel\n`;
    prompt += `• Color Palette: Warm, inviting tones - earth colors, soft pastels\n`;
    prompt += `• Typography: Friendly sans-serif, casual handwritten accents\n`;
    prompt += `• Motion Style: Natural, relaxed movements - not overly polished\n`;
    prompt += `• Visual Elements: Real-world footage, candid shots, relatable scenarios\n`;
    prompt += `• Transitions: Simple cuts, natural fades, seamless scene changes\n`;
    prompt += `• Camera Work: Natural handheld style, eye-level perspective\n`;
    prompt += `• Pace: Comfortable, conversational, allows for pauses and reactions\n`;
  } else if (tone === 'Technical') {
    prompt += `• Overall Aesthetic: Precise, informative, systematic presentation\n`;
    prompt += `• Color Palette: Technical blues, grays with highlight colors for emphasis\n`;
    prompt += `• Typography: Monospace elements, clear sans-serif for readability\n`;
    prompt += `• Motion Style: Methodical, step-by-step visual progression\n`;
    prompt += `• Visual Elements: Diagrams, schematics, technical illustrations, code snippets\n`;
    prompt += `• Transitions: Clear cuts between sections, deliberate reveals\n`;
    prompt += `• Camera Work: Stable, focused shots on technical details\n`;
    prompt += `• Pace: Detailed, allows time for technical information processing\n`;
  }
  prompt += `\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📝 DETAILED SCENE-BY-SCENE BREAKDOWN\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**SCENE 1: OPENING HOOK (0:00-0:05)**\n`;
  prompt += `Visual: Strong, attention-grabbing opening shot\n`;
  prompt += `• Use pattern interrupt - unexpected visual or statement\n`;
  prompt += `• Establish context immediately with environment/setting\n`;
  prompt += `• Show the "before" state or present the problem\n`;
  prompt += `• Include brand elements if applicable\n`;
  prompt += `Audio: Impactful music intro, voiceover with compelling question or statement\n`;
  prompt += `Text Overlay: Hook headline that makes viewer want to continue\n`;
  prompt += `Purpose: Capture attention within first 3 seconds to prevent scroll-away\n\n`;
  
  prompt += `**SCENE 2: CONTEXT & SETUP (0:05-0:15)**\n`;
  prompt += `Visual: Establish the situation, introduce main elements\n`;
  prompt += `• Show relatable scenario that ${targetAudience} recognizes\n`;
  prompt += `• Use B-roll footage that illustrates the problem/opportunity\n`;
  prompt += `• Include human element if appropriate - faces build connection\n`;
  prompt += `• Set the stage for the solution/information to come\n`;
  prompt += `Audio: Background music builds, voiceover explains relevance\n`;
  prompt += `Text Overlay: Key statistics, problem statements, or context points\n`;
  prompt += `Purpose: Make viewer understand why this matters to them\n\n`;
  
  prompt += `**SCENE 3: MAIN CONTENT DELIVERY (0:15-0:45)**\n`;
  prompt += `Visual: Core message presentation with supporting visuals\n`;
  prompt += `• Break content into 3-5 clear, distinct segments\n`;
  prompt += `• Use visual metaphors and analogies for complex concepts\n`;
  prompt += `• Include screen recordings, demonstrations, or examples\n`;
  prompt += `• Maintain visual variety to sustain engagement\n`;
  prompt += `• Use graphics, animations, or text to reinforce key points\n`;
  prompt += `Audio: Clear narration, sound effects for emphasis, music bed underneath\n`;
  prompt += `Text Overlay: Bullet points, key takeaways, important numbers\n`;
  prompt += `Purpose: Deliver valuable information in digestible, memorable format\n\n`;
  
  prompt += `**SCENE 4: PROOF & CREDIBILITY (0:45-0:55)**\n`;
  prompt += `Visual: Evidence that builds trust and authority\n`;
  prompt += `• Show results, testimonials, or case studies\n`;
  prompt += `• Include data visualizations, before/after comparisons\n`;
  prompt += `• Display credentials, certifications, or social proof\n`;
  prompt += `• Use customer quotes or success metrics\n`;
  prompt += `Audio: Testimonial audio clips or authoritative voiceover\n`;
  prompt += `Text Overlay: Statistics, testimonial quotes, key achievements\n`;
  prompt += `Purpose: Overcome skepticism and build viewer confidence\n\n`;
  
  prompt += `**SCENE 5: CALL TO ACTION & CLOSING (0:55-1:00)**\n`;
  prompt += `Visual: Clear, compelling CTA with easy next steps\n`;
  prompt += `• Show exactly what viewer should do next\n`;
  prompt += `• Display contact information, website, or links clearly\n`;
  prompt += `• Create sense of urgency or benefit to acting now\n`;
  prompt += `• End with memorable brand moment\n`;
  prompt += `Audio: Music crescendo, confident voiceover with clear direction\n`;
  prompt += `Text Overlay: Large, unmistakable CTA text with contact details\n`;
  prompt += `Purpose: Convert viewer interest into concrete action\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `⚙️ TECHNICAL SPECIFICATIONS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Resolution: 4K (3840x2160) for maximum quality, downscale to 1080p for delivery\n`;
  prompt += `• Aspect Ratio: \n`;
  prompt += `  - 16:9 (1920x1080) for YouTube, websites, presentations\n`;
  prompt += `  - 9:16 (1080x1920) for Instagram Reels, TikTok, Stories\n`;
  prompt += `  - 1:1 (1080x1080) for Instagram feed, Facebook posts\n`;
  prompt += `• Frame Rate: 30fps for standard content, 60fps for smooth motion, 24fps for cinematic\n`;
  prompt += `• Codec: H.264 for compatibility, H.265 for smaller file size\n`;
  prompt += `• Bitrate: 10-15 Mbps for 1080p, 20-30 Mbps for 4K\n`;
  prompt += `• Color Space: Rec. 709 for standard display, Rec. 2020 for HDR\n`;
  prompt += `• Audio: 48kHz, 16-bit stereo, -3dB peak levels, normalized loudness\n\n`;
  
  if (experienceLevel === 'Advanced') {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `🎥 ADVANCED PRODUCTION TECHNIQUES\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `• Color Grading: Apply cinematic LUTs, teal and orange complementary grading\n`;
    prompt += `• Motion Graphics: Use After Effects-style animations, kinetic typography\n`;
    prompt += `• Transitions: Custom transition effects, seamless scene morphing\n`;
    prompt += `• VFX Elements: Particle effects, light leaks, lens flares (tasteful)\n`;
    prompt += `• 3D Elements: Integrate 3D graphics, product renders where appropriate\n`;
    prompt += `• Audio Design: Layer multiple audio tracks, use professional sound design\n`;
    prompt += `• Speed Ramping: Strategic slow-motion or speed-up for emphasis\n`;
    prompt += `• Masking & Compositing: Advanced layering techniques\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎵 AUDIO PRODUCTION DETAILS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Background Music: Select track that matches ${tone.toLowerCase()} tone and energy level\n`;
  prompt += `  - Use royalty-free music from Epidemic Sound, Artlist, or similar\n`;
  prompt += `  - Keep music volume at 20-30% when voiceover is present\n`;
  prompt += `  - Build music intensity to match video pacing\n`;
  prompt += `• Voiceover: Professional, clear narration optimized for ${targetAudience}\n`;
  prompt += `  - Use pop filter and noise reduction\n`;
  prompt += `  - Match voice energy to visual content\n`;
  prompt += `  - Leave appropriate pauses for visual impact\n`;
  prompt += `• Sound Effects: Strategic use for emphasis and transitions\n`;
  prompt += `  - Whooshes for scene transitions\n`;
  prompt += `  - Click sounds for UI interactions\n`;
  prompt += `  - Ambient sounds for environmental scenes\n`;
  prompt += `• Audio Mixing: Professional balance between all audio elements\n\n`;
  
  if (constraints && constraints.trim()) {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `⚠️ FINAL CONSTRAINTS CHECK\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `Before finalizing your video plan, ensure it meets ALL these requirements:\n\n`;
    prompt += `${constraints}\n\n`;
    prompt += `If any constraint cannot be met, explain why and suggest alternatives. Otherwise, confirm all constraints are incorporated.\n\n`;
  }
  
  prompt += `Create this video with maximum production value, optimized for ${targetAudience}, maintaining ${tone.toLowerCase()} tone throughout, and ensuring every second delivers value`;
  
  if (constraints && constraints.trim()) {
    prompt += ` while strictly adhering to all specified constraints`;
  }
  prompt += `.`;
  
  return prompt;
}

function generateCodePrompt(answers) {
  const { outcome, experienceLevel, targetAudience, tone, constraints } = answers;
  
  let prompt = `Develop a complete, production-ready code solution for: ${outcome}\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `💻 PROJECT SPECIFICATION\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Primary Objective: ${outcome}\n`;
  prompt += `• End Users: ${targetAudience}\n`;
  prompt += `• Code Quality Standard: ${tone === 'Professional' ? 'Enterprise production-ready' : tone === 'Technical' ? 'Performance-optimized, technically excellent' : tone === 'Creative' ? 'Innovative with modern patterns' : 'Clean, maintainable, well-documented'}\n`;
  prompt += `• Developer Experience Level: ${experienceLevel}\n`;
  if (constraints && constraints.trim()) {
    prompt += `\n⚠️ CRITICAL TECHNICAL CONSTRAINTS:\n`;
    prompt += `${constraints}\n`;
    prompt += `⚠️ Code implementation MUST adhere to these constraints without exception.\n`;
  }
  prompt += `\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🏗️ ARCHITECTURE & DESIGN\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**System Architecture:**\n`;
  prompt += `• Choose appropriate architectural pattern:\n`;
  prompt += `  - MVC/MVVM for UI applications\n`;
  prompt += `  - Microservices for distributed systems\n`;
  prompt += `  - Layered architecture for traditional applications\n`;
  prompt += `  - Clean Architecture for maintainability\n`;
  prompt += `• Define clear separation of concerns\n`;
  prompt += `• Identify core components and their responsibilities\n`;
  prompt += `• Establish data flow and communication patterns\n`;
  prompt += `• Plan for scalability from the start\n\n`;
  
  prompt += `**Design Patterns:**\n`;
  prompt += `Apply appropriate patterns for the use case:\n`;
  prompt += `• Creational: Factory, Builder, Singleton (use judiciously)\n`;
  prompt += `• Structural: Adapter, Decorator, Facade\n`;
  prompt += `• Behavioral: Observer, Strategy, Command\n`;
  prompt += `• Choose patterns that solve actual problems, not for pattern's sake\n\n`;
  
  prompt += `**File & Directory Structure:**\n`;
  prompt += `Organize code logically:\n`;
  prompt += `\`\`\`\n`;
  prompt += `project-root/\n`;
  prompt += `├── src/\n`;
  prompt += `│   ├── components/     # Reusable components\n`;
  prompt += `│   ├── services/       # Business logic\n`;
  prompt += `│   ├── models/         # Data models\n`;
  prompt += `│   ├── utils/          # Utility functions\n`;
  prompt += `│   ├── config/         # Configuration\n`;
  prompt += `│   └── tests/          # Test files\n`;
  prompt += `├── docs/               # Documentation\n`;
  prompt += `├── README.md\n`;
  prompt += `└── package.json/requirements.txt\n`;
  prompt += `\`\`\`\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📝 CODE IMPLEMENTATION REQUIREMENTS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**Code Quality Standards:**\n`;
  prompt += `• Write self-documenting code with clear variable/function names\n`;
  prompt += `• Follow language-specific style guides (PEP 8 for Python, Airbnb for JavaScript, etc.)\n`;
  prompt += `• Keep functions small and focused (single responsibility)\n`;
  prompt += `• Limit function parameters (max 3-4, use objects for more)\n`;
  prompt += `• Avoid deep nesting (max 3-4 levels)\n`;
  prompt += `• Use meaningful abstractions, avoid premature optimization\n`;
  prompt += `• Write code that is easy to delete and refactor\n\n`;
  
  prompt += `**Documentation Standards:**\n`;
  if (experienceLevel === 'Beginner') {
    prompt += `• Add extensive comments explaining WHY, not just WHAT\n`;
    prompt += `• Include inline explanations for complex logic\n`;
    prompt += `• Document every function with purpose, parameters, return values\n`;
    prompt += `• Provide usage examples for each major component\n`;
    prompt += `• Include learning resources and references\n`;
    prompt += `• Explain key concepts and terminology\n`;
  } else if (experienceLevel === 'Intermediate') {
    prompt += `• Document public APIs and interfaces\n`;
    prompt += `• Explain complex algorithms and business logic\n`;
    prompt += `• Include JSDoc/docstrings for functions and classes\n`;
    prompt += `• Provide architectural overview\n`;
    prompt += `• Document important design decisions\n`;
  } else if (experienceLevel === 'Advanced') {
    prompt += `• Focus documentation on "why" and architectural decisions\n`;
    prompt += `• Include performance considerations and trade-offs\n`;
    prompt += `• Document complex algorithms with Big O analysis\n`;
    prompt += `• Provide extension points and customization guide\n`;
    prompt += `• Include contribution guidelines\n`;
  }
  prompt += `\n`;
  
  prompt += `**Error Handling Strategy:**\n`;
  prompt += `• Implement comprehensive try-catch blocks\n`;
  prompt += `• Use specific exception types, not generic catch-all\n`;
  prompt += `• Provide meaningful error messages for ${targetAudience}\n`;
  prompt += `• Log errors appropriately (with context, without sensitive data)\n`;
  prompt += `• Implement graceful degradation where possible\n`;
  prompt += `• Add input validation at boundaries\n`;
  prompt += `• Handle edge cases explicitly\n`;
  prompt += `• Implement retry logic for transient failures\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🔒 SECURITY & PERFORMANCE\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**Security Best Practices:**\n`;
  prompt += `• Validate and sanitize ALL user inputs\n`;
  prompt += `• Use parameterized queries to prevent SQL injection\n`;
  prompt += `• Implement proper authentication and authorization\n`;
  prompt += `• Store sensitive data securely (hash passwords, encrypt data)\n`;
  prompt += `• Use HTTPS for all network communication\n`;
  prompt += `• Implement rate limiting for APIs\n`;
  prompt += `• Follow OWASP Top 10 security practices\n`;
  prompt += `• Keep dependencies updated, scan for vulnerabilities\n`;
  prompt += `• Never commit secrets to version control\n\n`;
  
  prompt += `**Performance Optimization:**\n`;
  prompt += `• Use appropriate data structures (O(1) lookups where needed)\n`;
  prompt += `• Implement caching strategies for expensive operations\n`;
  prompt += `• Use lazy loading for large datasets\n`;
  prompt += `• Optimize database queries (indexes, query optimization)\n`;
  prompt += `• Minimize network requests, batch when possible\n`;
  prompt += `• Use asynchronous operations for I/O-bound tasks\n`;
  prompt += `• Profile code to identify bottlenecks\n`;
  prompt += `• Optimize critical paths, accept "good enough" elsewhere\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🧪 TESTING STRATEGY\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**Test Coverage:**\n`;
  prompt += `• Unit Tests: Test individual functions and methods\n`;
  prompt += `  - Test happy paths and edge cases\n`;
  prompt += `  - Test error conditions and exceptions\n`;
  prompt += `  - Aim for 80%+ code coverage on critical paths\n`;
  prompt += `• Integration Tests: Test component interactions\n`;
  prompt += `  - Test API endpoints\n`;
  prompt += `  - Test database operations\n`;
  prompt += `  - Test external service integrations\n`;
  prompt += `• End-to-End Tests: Test complete user workflows\n`;
  prompt += `  - Test critical user journeys\n`;
  prompt += `  - Test across different browsers/devices if applicable\n\n`;
  
  if (experienceLevel === 'Advanced') {
    prompt += `**Advanced Testing Techniques:**\n`;
    prompt += `• Property-based testing for complex logic\n`;
    prompt += `• Performance testing and benchmarking\n`;
    prompt += `• Security testing (penetration testing basics)\n`;
    prompt += `• Chaos engineering principles for resilience\n`;
    prompt += `• Contract testing for microservices\n`;
    prompt += `• Visual regression testing for UI\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📦 DELIVERABLES\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**1. Complete Source Code**\n`;
  prompt += `Provide all necessary files:\n`;
  prompt += `• Main application code with proper structure\n`;
  prompt += `• Configuration files\n`;
  prompt += `• Test files\n`;
  prompt += `• Build scripts\n`;
  prompt += `• Environment setup files\n\n`;
  
  prompt += `**2. README Documentation**\n`;
  prompt += `Include comprehensive README with:\n`;
  prompt += `• Project overview and purpose\n`;
  prompt += `• Prerequisites and dependencies\n`;
  prompt += `• Installation instructions (step-by-step)\n`;
  prompt += `• Configuration guide\n`;
  prompt += `• Usage examples with code snippets\n`;
  prompt += `• API documentation if applicable\n`;
  prompt += `• Troubleshooting common issues\n`;
  prompt += `• Contributing guidelines\n`;
  prompt += `• License information\n\n`;
  
  prompt += `**3. Setup Instructions**\n`;
  prompt += `Provide detailed steps:\n`;
  prompt += `\`\`\`bash\n`;
  prompt += `# 1. Clone repository\n`;
  prompt += `# 2. Install dependencies\n`;
  prompt += `# 3. Configure environment\n`;
  prompt += `# 4. Run migrations/setup\n`;
  prompt += `# 5. Start application\n`;
  prompt += `# 6. Run tests\n`;
  prompt += `\`\`\`\n\n`;
  
  prompt += `**4. Usage Examples**\n`;
  prompt += `Provide practical code examples:\n`;
  prompt += `• Basic usage scenarios\n`;
  prompt += `• Common use cases\n`;
  prompt += `• Advanced configurations\n`;
  prompt += `• Integration examples\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `⚡ IMPLEMENTATION PRIORITIES & FINAL REQUIREMENTS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Focus on solving the core problem: ${outcome}\n`;
  prompt += `• Optimize for ${targetAudience} experience\n`;
  prompt += `• Maintain ${tone.toLowerCase()} code quality standards\n`;
  prompt += `• Ensure code is ${experienceLevel === 'Beginner' ? 'beginner-friendly with extensive explanations' : experienceLevel === 'Intermediate' ? 'well-balanced between clarity and sophistication' : 'technically excellent with advanced patterns'}\n`;
  prompt += `• Prioritize maintainability and readability\n`;
  prompt += `• Make it production-ready, not just a proof-of-concept\n\n`;
  
  if (constraints && constraints.trim()) {
    prompt += `⚠️ CRITICAL: Your code implementation MUST satisfy these constraints:\n`;
    prompt += `${constraints}\n\n`;
    prompt += `**VALIDATION CHECKLIST:**\n`;
    prompt += `Before providing your code, verify:\n`;
    prompt += `□ Does the code meet ALL the constraints listed above?\n`;
    prompt += `□ Are there any conflicts between constraints and standard practices?\n`;
    prompt += `□ Have you incorporated constraint requirements into architecture decisions?\n`;
    prompt += `□ Does the documentation explain how constraints are satisfied?\n\n`;
    prompt += `If you cannot meet a constraint, explicitly state why and propose alternatives.\n\n`;
  }
  
  prompt += `Begin implementation now, providing complete, runnable, well-documented code that exceeds professional standards`;
  
  if (constraints && constraints.trim()) {
    prompt += ` and fully complies with all specified constraints`;
  }
  prompt += `.`;
  
  return prompt;
}

function generateStandardPrompt(answers) {
  const { useCase, outcome, experienceLevel, targetAudience, tone, constraints } = answers;
  
  const roleMap = {
    'Content Creation': 'an expert content strategist, creative writer, and storytelling specialist',
    'Freelancing': 'a professional freelance consultant, business advisor, and career strategist',
    'Business Growth': 'a strategic business consultant, growth specialist, and market expansion expert',
    'Digital Marketing': 'a senior digital marketing strategist, campaign expert, and conversion optimization specialist',
    'Income Growth': 'a monetization strategist, income diversification expert, and financial growth consultant',
    'Customer Support': 'a customer success expert, support systems specialist, and service excellence consultant',
    'E-commerce': 'an e-commerce strategist, online retail expert, and conversion rate optimization specialist',
    'Data Analysis': 'a data scientist, analytics expert, and insights strategist',
    'Social Media Management': 'a social media strategist, community engagement specialist, and brand builder',
    'SEO & Content Strategy': 'an SEO expert, content marketing strategist, and organic growth specialist'
  };
  
  const role = roleMap[useCase] || 'an expert advisor';
  
  // Ultra-detailed AI instruction prompt
  let prompt = `You are ${role} with 10+ years of proven expertise and a track record of delivering exceptional results. Your mission is to help me achieve this specific outcome:\n\n`;
  prompt += `🎯 PRIMARY OBJECTIVE: "${outcome}"\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📊 PROJECT CONTEXT & PARAMETERS\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `• Target Audience: ${targetAudience}\n`;
  prompt += `• Required Tone: ${tone} (maintain this consistently throughout)\n`;
  prompt += `• My Experience Level: ${experienceLevel} (adapt complexity accordingly)\n`;
  if (constraints && constraints.trim()) {
    prompt += `\n⚠️⚠️⚠️ CRITICAL CONSTRAINTS - HIGHEST PRIORITY ⚠️⚠️⚠️\n`;
    prompt += `These requirements MUST be integrated into every aspect of your response:\n\n`;
    prompt += `${constraints}\n\n`;
    prompt += `⚠️ ENFORCEMENT:\n`;
    prompt += `• These constraints take precedence over all other instructions\n`;
    prompt += `• Build your entire response around satisfying these constraints\n`;
    prompt += `• If a constraint conflicts with other guidelines, the constraint wins\n`;
    prompt += `• Verify compliance before finalizing your response\n\n`;
  }
  prompt += `• Success Criteria: Practical, immediately actionable, measurable results\n\n`;
  
  // Ultra-detailed use-case specific instructions
  if (useCase === 'Content Creation') {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `✍️ CONTENT CREATION FRAMEWORK\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    prompt += `**1. AUDIENCE DEEP DIVE**\n`;
    prompt += `Analyze ${targetAudience} thoroughly:\n`;
    prompt += `• Demographics: Age, location, profession, income level\n`;
    prompt += `• Psychographics: Values, beliefs, aspirations, pain points, fears\n`;
    prompt += `• Behavioral patterns: Content consumption habits, preferred platforms, engagement times\n`;
    prompt += `• Language style: Vocabulary level, preferred communication style, cultural context\n`;
    prompt += `• Current knowledge level: What they know vs. what they need to know\n`;
    prompt += `• Emotional triggers: What motivates them to take action\n\n`;
    
    prompt += `**2. CONTENT STRUCTURE**\n`;
    prompt += `Create a compelling narrative arc:\n`;
    prompt += `• Hook (first 3-5 seconds): Grab attention with pattern interrupt, provocative question, or surprising statement\n`;
    prompt += `• Problem identification: Clearly articulate the pain point or opportunity\n`;
    prompt += `• Context building: Provide background and relevance\n`;
    prompt += `• Value proposition: Explain why this matters and what they'll gain\n`;
    prompt += `• Core content: Deliver main insights with clear structure (listicles, how-to, case studies)\n`;
    prompt += `• Evidence & proof: Include data, examples, testimonials, case studies\n`;
    prompt += `• Actionable takeaways: Specific steps they can implement immediately\n`;
    prompt += `• Strong CTA: Clear next step with compelling reason to act now\n\n`;
    
    prompt += `**3. ENGAGEMENT OPTIMIZATION**\n`;
    prompt += `Maximize reader engagement:\n`;
    prompt += `• Use power words that trigger emotional responses\n`;
    prompt += `• Break up text with subheadings every 100-150 words\n`;
    prompt += `• Include bullet points and numbered lists for scannability\n`;
    prompt += `• Add relevant examples, analogies, and metaphors\n`;
    prompt += `• Use "you" language to create personal connection\n`;
    prompt += `• Include open loops to maintain curiosity\n`;
    prompt += `• Add surprising facts or contrarian viewpoints\n`;
    prompt += `• Use storytelling techniques to make content memorable\n\n`;
    
    prompt += `**4. ${tone.toUpperCase()} TONE EXECUTION**\n`;
    if (tone === 'Professional') {
      prompt += `• Use industry-specific terminology appropriately\n`;
      prompt += `• Maintain authoritative voice without being condescending\n`;
      prompt += `• Include data, statistics, and credible sources\n`;
      prompt += `• Structure with clear hierarchy and logical flow\n`;
      prompt += `• Avoid colloquialisms, keep language precise\n`;
    } else if (tone === 'Casual') {
      prompt += `• Write as if having a friendly conversation\n`;
      prompt += `• Use contractions and everyday language\n`;
      prompt += `• Include personal anecdotes and relatable examples\n`;
      prompt += `• Add humor where appropriate\n`;
      prompt += `• Be warm and approachable in tone\n`;
    } else if (tone === 'Creative') {
      prompt += `• Use vivid imagery and descriptive language\n`;
      prompt += `• Employ literary devices (metaphors, similes, alliteration)\n`;
      prompt += `• Create unexpected connections and fresh perspectives\n`;
      prompt += `• Use dynamic, active verbs\n`;
      prompt += `• Craft memorable phrases and quotable moments\n`;
    } else if (tone === 'Technical') {
      prompt += `• Use precise technical terminology correctly\n`;
      prompt += `• Include specifications, metrics, and technical details\n`;
      prompt += `• Provide step-by-step technical explanations\n`;
      prompt += `• Reference standards, best practices, frameworks\n`;
      prompt += `• Maintain accuracy and technical rigor\n`;
    }
    prompt += `\n`;
    
  } else if (useCase === 'Digital Marketing') {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `📱 DIGITAL MARKETING STRATEGY BLUEPRINT\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    prompt += `**1. AUDIENCE SEGMENTATION**\n`;
    prompt += `Profile ${targetAudience} in detail:\n`;
    prompt += `• Create detailed buyer personas with demographics, behaviors, motivations\n`;
    prompt += `• Map customer journey from awareness to conversion to advocacy\n`;
    prompt += `• Identify pain points at each stage of the funnel\n`;
    prompt += `• Determine optimal touchpoints and channels\n`;
    prompt += `• Analyze competitors' positioning and messaging to ${targetAudience}\n\n`;
    
    prompt += `**2. CAMPAIGN ARCHITECTURE**\n`;
    prompt += `• Objective: Clearly defined, measurable goal\n`;
    prompt += `• Strategy: Overarching approach to achieve objective\n`;
    prompt += `• Tactics: Specific actions on each platform\n`;
    prompt += `• Budget allocation: Distribution across channels based on ROI potential\n`;
    prompt += `• Timeline: Phased rollout with key milestones\n`;
    prompt += `• Success metrics: KPIs for each campaign element\n\n`;
    
    prompt += `**3. PLATFORM-SPECIFIC EXECUTION**\n`;
    prompt += `Tailor content for each platform:\n`;
    prompt += `• Facebook/Instagram: Visual storytelling, community building, retargeting\n`;
    prompt += `• LinkedIn: Professional thought leadership, B2B targeting\n`;
    prompt += `• Twitter/X: Real-time engagement, trending topics, customer service\n`;
    prompt += `• TikTok: Short-form video, trend participation, authentic personality\n`;
    prompt += `• Google Ads: Intent-based search, remarketing, shopping ads\n`;
    prompt += `• Email: Segmented campaigns, automation sequences, personalization\n\n`;
    
    prompt += `**4. CONVERSION OPTIMIZATION**\n`;
    prompt += `• Landing pages: Clear value prop, single CTA, social proof, urgency\n`;
    prompt += `• A/B testing plan: Headlines, CTAs, images, copy variations\n`;
    prompt += `• Funnel optimization: Reduce friction at each conversion point\n`;
    prompt += `• Retargeting strategy: Custom audiences, sequential messaging\n`;
    prompt += `• Analytics setup: Track every touchpoint, attribute conversions correctly\n\n`;
    
  } else if (useCase === 'Business Growth') {
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    prompt += `📈 BUSINESS GROWTH STRATEGIC FRAMEWORK\n`;
    prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    prompt += `**1. CURRENT STATE ANALYSIS**\n`;
    prompt += `• Market position: Where you stand vs. competitors\n`;
    prompt += `• Strengths: Core competencies and competitive advantages\n`;
    prompt += `• Weaknesses: Gaps and areas needing improvement\n`;
    prompt += `• Opportunities: Market trends, unmet needs, emerging segments\n`;
    prompt += `• Threats: Competition, market shifts, economic factors\n`;
    prompt += `• Financial baseline: Current revenue, margins, cash flow\n\n`;
    
    prompt += `**2. GROWTH OPPORTUNITIES EVALUATION**\n`;
    prompt += `Identify and prioritize growth vectors:\n`;
    prompt += `• Market penetration: Increase share in existing markets\n`;
    prompt += `• Market development: Enter new geographic or demographic markets\n`;
    prompt += `• Product development: New offerings for existing customers\n`;
    prompt += `• Diversification: New products for new markets\n`;
    prompt += `• Strategic partnerships: Collaborations that amplify reach\n`;
    prompt += `• Operational efficiency: Improve margins through optimization\n\n`;
    
    prompt += `**3. STRATEGIC ROADMAP**\n`;
    prompt += `• 30-day quick wins: Immediate actions with high impact\n`;
    prompt += `• 90-day initiatives: Foundation-building projects\n`;
    prompt += `• 1-year milestones: Major strategic objectives\n`;
    prompt += `• Resource allocation: Budget, team, technology needs\n`;
    prompt += `• Risk mitigation: Contingency plans for each initiative\n`;
    prompt += `• Success metrics: Leading and lagging indicators\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `📋 DELIVERABLE STRUCTURE\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `**SECTION 1: EXECUTIVE SUMMARY** (3-5 sentences)\n`;
  prompt += `Provide high-level overview of approach and expected outcomes\n\n`;
  
  prompt += `**SECTION 2: IMMEDIATE ACTION PLAN** (Prioritized)\n`;
  prompt += `List 5-7 specific actions to implement in the next 24-48 hours:\n`;
  prompt += `For each action, include:\n`;
  prompt += `• Exact step-by-step instructions\n`;
  prompt += `• Required resources/tools\n`;
  prompt += `• Time estimate\n`;
  prompt += `• Expected outcome\n`;
  prompt += `• Success measurement\n\n`;
  
  prompt += `**SECTION 3: DETAILED IMPLEMENTATION GUIDE**\n`;
  prompt += `Comprehensive breakdown of the entire process:\n`;
  prompt += `• Phase 1: Foundation & Setup\n`;
  prompt += `• Phase 2: Core Execution\n`;
  prompt += `• Phase 3: Optimization & Scaling\n`;
  prompt += `Include timeline, dependencies, checkpoints for each phase\n\n`;
  
  prompt += `**SECTION 4: TEMPLATES & EXAMPLES**\n`;
  prompt += `Provide 3-5 ready-to-use templates or detailed examples:\n`;
  prompt += `• Fill-in-the-blank templates\n`;
  prompt += `• Real-world examples with annotations\n`;
  prompt += `• Before/after comparisons\n`;
  prompt += `• Best practice demonstrations\n\n`;
  
  prompt += `**SECTION 5: SUCCESS METRICS & KPIs**\n`;
  prompt += `Define clear measurements:\n`;
  prompt += `• Primary KPIs: Main success indicators\n`;
  prompt += `• Secondary metrics: Supporting measurements\n`;
  prompt += `• Benchmarks: Industry standards and targets\n`;
  prompt += `• Tracking method: How to monitor progress\n`;
  prompt += `• Review frequency: When and how to assess\n\n`;
  
  prompt += `**SECTION 6: COMMON PITFALLS & SOLUTIONS**\n`;
  prompt += `Identify 5-7 common mistakes and how to avoid them:\n`;
  prompt += `• Mistake description\n`;
  prompt += `• Why it happens\n`;
  prompt += `• How to prevent it\n`;
  prompt += `• Recovery strategy if it occurs\n\n`;
  
  prompt += `**SECTION 7: RESOURCES & NEXT STEPS**\n`;
  prompt += `• Recommended tools and platforms\n`;
  prompt += `• Additional learning resources\n`;
  prompt += `• Community support options\n`;
  prompt += `• Clear action sequence for getting started\n\n`;
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `⚡ FINAL OUTPUT REQUIREMENTS - MUST FOLLOW EXACTLY\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `Your response MUST meet ALL of the following requirements:\n\n`;
  
  prompt += `**Audience & Complexity:**\n`;
  prompt += `• Adapt ALL content to ${experienceLevel} level understanding\n`;
  prompt += `• Use ${tone.toLowerCase()} language and tone throughout every section\n`;
  prompt += `• Ensure everything is specifically tailored for ${targetAudience}\n\n`;
  
  prompt += `**Content Quality Standards:**\n`;
  prompt += `• Make every recommendation immediately actionable - no vague advice\n`;
  prompt += `• Include specific numbers, timeframes, and measurable metrics\n`;
  prompt += `• Provide real, concrete examples - never abstract concepts\n`;
  prompt += `• Be comprehensive yet organized and scannable\n`;
  prompt += `• Focus on practical implementation over theoretical discussion\n\n`;
  
  if (constraints && constraints.trim()) {
    prompt += `**⚠️ MANDATORY USER-SPECIFIED CONSTRAINTS:**\n`;
    prompt += `These requirements are CRITICAL and must be followed WITHOUT EXCEPTION:\n\n`;
    prompt += `${constraints}\n\n`;
    prompt += `⚠️ ENFORCEMENT RULES:\n`;
    prompt += `• Review your complete response BEFORE sending\n`;
    prompt += `• Verify EVERY constraint above has been satisfied\n`;
    prompt += `• If you cannot meet ALL constraints, explicitly state which constraint conflicts and why\n`;
    prompt += `• Do NOT provide a response that violates any of these constraints\n`;
    prompt += `• These user constraints OVERRIDE any conflicting instructions above\n\n`;
  }
  
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  prompt += `🎯 BEGIN YOUR RESPONSE\n`;
  prompt += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  prompt += `Objective: "${outcome}"\n\n`;
  
  if (constraints && constraints.trim()) {
    prompt += `REMINDER: Your response must strictly adhere to these constraints:\n${constraints}\n\n`;
  }
  
  prompt += `Now provide your complete response following ALL requirements above.`;
  
  return prompt;
}
